import { AfterContentInit, ChangeDetectorRef, Component, ContentChildren, EventEmitter, Input, OnDestroy, Output, QueryList, inject, signal } from '@angular/core';
import { Subscription } from 'rxjs';

/** One tab of <app-tab-group> (replacement for mat-tab). Its content shows only while the tab is active. */
@Component({
  selector: 'app-tab',
  standalone: false,
  template: `
    @if (active()) {
      <ng-content></ng-content>
    }
  `,
})
export class AppTabComponent {
  @Input() label = '';
  @Input() disabled = false;
  /** Set by the group. A signal so the change is safe to make from the group's lifecycle hooks. */
  readonly active = signal(false);
}

/** Tab strip on the OneVedAI tabs plus the active panel (replacement for mat-tab-group). */
@Component({
  selector: 'app-tab-group',
  standalone: false,
  template: `
    <onevedai-tabs [value]="'' + selectedIndex" (valueChange)="select(+$event)">
      @for (tab of tabList; track $index) {
        <onevedai-tab [label]="tab.label" [value]="'' + $index" [disabled]="tab.disabled"></onevedai-tab>
      }
    </onevedai-tabs>
    <div class="panel"><ng-content></ng-content></div>
  `,
  styles: [':host { display: block; } .panel { padding-top: var(--space-3); }'],
})
export class AppTabGroupComponent implements AfterContentInit, OnDestroy {
  private _selectedIndex = 0;
  @Input() set selectedIndex(value: number) {
    this._selectedIndex = value ?? 0;
    this.syncActive();
  }
  get selectedIndex(): number {
    return this._selectedIndex;
  }
  @Output() selectedIndexChange = new EventEmitter<number>();
  /** MatTabGroup API compatibility: `(selectedTabChange)="f($event.index)"`. */
  @Output() selectedTabChange = new EventEmitter<{ index: number; tab: { textLabel: string } }>();
  @ContentChildren(AppTabComponent) tabs!: QueryList<AppTabComponent>;

  private sub?: Subscription;
  private readonly cdr = inject(ChangeDetectorRef);

  get tabList(): AppTabComponent[] {
    return this.tabs?.toArray() ?? [];
  }

  ngAfterContentInit(): void {
    this.syncActive();
    this.sub = this.tabs.changes.subscribe(() => {
      this.syncActive();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  select(index: number): void {
    if (Number.isNaN(index) || index === this._selectedIndex) return;
    this._selectedIndex = index;
    this.syncActive();
    this.selectedIndexChange.emit(index);
    this.selectedTabChange.emit({ index, tab: { textLabel: this.tabList[index]?.label ?? '' } });
  }

  private syncActive(): void {
    this.tabs?.forEach((tab, i) => tab.active.set(i === this._selectedIndex));
  }
}
